import { isValidJson } from '@/src/api';
import type { ApiResponse } from '@/src/core/rest-api/types';
import { clientAdminCreateSchema, type ClientAdmin } from '@/src/domain/entities';

function messageFromApiError(data: ApiResponse): string {
  const raw = data.details;
  if (Array.isArray(raw) && raw.length > 0) {
    const messages = raw
      .map((item) =>
        typeof item === 'object' &&
        item !== null &&
        'message' in item &&
        typeof (item as { message: unknown }).message === 'string'
          ? (item as { message: string }).message
          : ''
      )
      .filter(Boolean);
    if (messages.length > 0) {
      return [...new Set(messages)].join(' ');
    }
  }
  return data.error || data.message || 'Request failed';
}

const basePath = (clientId: string) => `${process.env.NEXT_PUBLIC_API_BASE_URL}/clients/${clientId}/admin-emails`;

export const clientAdmin = {
  list: async (clientId: string): Promise<ClientAdmin[]> => {
    const response = await fetch(basePath(clientId), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': `${process.env.SERIVCE_X_CSRF_TOKEN}`
      }
    });
    if (!response.ok) return [];
    const responseText = await response.text();
    if (!isValidJson(responseText)) return [];
    const data: ApiResponse<ClientAdmin[]> = JSON.parse(responseText);
    return (data.success && data.data) || [];
  },

  add: async (clientId: string, email: string): Promise<{ admin?: ClientAdmin; error?: string }> => {
    const parsed = clientAdminCreateSchema.safeParse({ email: email.trim() });
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      return { error: first?.message ?? 'Invalid email' };
    }

    const response = await fetch(basePath(clientId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': `${process.env.SERIVCE_X_CSRF_TOKEN}`
      },
      body: JSON.stringify({ email: parsed.data.email })
    });
    const responseText = await response.text();
    if (!isValidJson(responseText)) {
      return { error: 'Invalid response' };
    }
    const data: ApiResponse<ClientAdmin> = JSON.parse(responseText);
    if (response.ok && data.success && data.data) {
      return { admin: data.data };
    }
    return { error: messageFromApiError(data) };
  },

  remove: async (clientId: string, email: string): Promise<{ ok: boolean; error?: string }> => {
    const url = new URL(basePath(clientId));
    url.searchParams.set('email', email);
    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers: {
        'x-csrf-token': `${process.env.SERIVCE_X_CSRF_TOKEN}`
      }
    });
    const responseText = await response.text();
    if (response.ok) {
      return { ok: true };
    }
    if (isValidJson(responseText)) {
      const data: ApiResponse<unknown> = JSON.parse(responseText);
      return { ok: false, error: messageFromApiError(data) };
    }
    return { ok: false, error: 'Request failed' };
  }
};
